import { $http } from "@/utils/https";
import type { ActionContext } from "vuex";
import type { RootState } from "@/store";

const IMAGE_PLACEHOLDER = "images/default/upload_image.png";

interface PageContent {
  id: number;
  type: string;
  position: number;
  title: string;
  description: string;
  image: string;
  buttonText: string;
  buttonLink: string;
}

interface PageFeedback {
  id: number;
  position: number;
  name: string;
  role: string;
  text: string;
  image: string;
}

interface PageFaq {
  id: number;
  position: number;
  question: string;
  answer: string;
}

interface ForumPage {
  id: number;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  pageContents: { data: Array<PageContent> };
  pageFeedbacks: { data: Array<PageFeedback> };
  pageFaqs: { data: Array<PageFaq> };
}

interface Person {
  id: number;
  name: string;
  bio: string;
  image: string;
}

interface Panel {
  id: number;
  title: string;
  description: string;
  moderators: Array<Person>;
  participants: Array<Person>;
}

interface State {
  page: ForumPage | null;
}

const state: State = {
  page: null,
};

function byPosition<T extends { position: number }>(items?: Array<T>): Array<T> {
  return [...(items || [])].sort((a, b) => a.position - b.position);
}

const toImage = (value?: string): string => (!value || value.endsWith(IMAGE_PLACEHOLDER) ? "" : value);

const trimHtml = (value?: string): string => (value || "").replace(/(\s|&nbsp;|<br\s*\/?>)+$/gi, "");

const splitSpeaker = (text?: string) => {
  const match = /^\s*<b>([\s\S]*?)<\/b>([\s\S]*)$/i.exec(text || "");

  return match ? { person: match[1].trim(), bio: trimHtml(match[2]) } : { person: "", bio: trimHtml(text) };
};

const toTalk = (feedback: PageFeedback) => ({
  id: feedback.id,
  topic: feedback.name,
  desc: feedback.role,
  image: toImage(feedback.image),
  ...splitSpeaker(feedback.text),
});

const toPerson = (content: PageContent): Person => ({
  id: content.id,
  name: content.title,
  bio: trimHtml(content.description),
  image: toImage(content.image),
});

const contentsOf = (state: State) => byPosition(state.page?.pageContents?.data);

const talksOf = (state: State) => byPosition(state.page?.pageFeedbacks?.data).map(toTalk);

const getters = {
  page: (state: State) => state.page,
  lectorium: (state: State) => talksOf(state).slice(0, -1),
  masterClass: (state: State) => talksOf(state).slice(-1)[0] || null,
  faqs: (state: State) => byPosition(state.page?.pageFaqs?.data),
  panels: (state: State) => {
    const panels: Array<Panel> = [];

    contentsOf(state).forEach(content => {
      if (content.type === "panel_title") {
        panels.push({
          id: content.id,
          title: content.title,
          description: trimHtml(content.description),
          moderators: [],
          participants: [],
        });
        return;
      }

      const panel = panels[panels.length - 1];
      if (!panel) return;

      if (content.type === "moderator") panel.moderators.push(toPerson(content));
      if (content.type === "participant") panel.participants.push(toPerson(content));
    });

    return panels;
  },
  practice: (state: State) => {
    const contents = contentsOf(state);
    const intro = contents.find(content => content.type === "block_with_image_with_button");
    const how = contents.find(content => content.type === "title_with_center_description");

    return {
      title: intro?.title || "",
      image: toImage(intro?.image),
      buttonText: intro?.buttonText || "",
      buttonLink: intro?.buttonLink || "",
      howTitle: how?.title || "",
      howDescription: trimHtml(how?.description),
      stats: contents
        .filter(content => content.type === "stat")
        .map(content => ({ id: content.id, number: content.title, list: trimHtml(content.description) })),
      steps: contents
        .filter(content => content.type === "step")
        .map(content => ({ id: content.id, title: content.title })),
    };
  },
};

const actions = {
  GET_PAGE: async ({ commit }: ActionContext<State, RootState>) => {
    const response = await $http.get<{ data: ForumPage }>("v1/pages/forum");
    commit("SET_PAGE", response.data.data);

    return response.data.data;
  },
};

const mutations = {
  SET_PAGE(state: State, payload: ForumPage) {
    state.page = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
